// Import necessary libraries and components
import React, { useEffect, useState } from 'react';
import api from '../../../../api';
import {
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  PresentationChartBarIcon,
  CheckBadgeIcon,
  MagnifyingGlassCircleIcon,
} from '@heroicons/react/24/outline';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';

// Define the card configuration
const cardData = [
  { title: 'Production', icon: ChartBarSquareIcon, key: 'totalProduction' },
  { title: 'Sampling Count', icon: ClipboardDocumentListIcon, key: 'totalSamplingCount' },
  { title: 'Audited', icon: ClipboardDocumentCheckIcon, key: 'auditorTarget' },
  { title: 'Error Count', icon: ExclamationCircleIcon, key: 'errorCount' },
  { title: 'Sampling %', icon: PresentationChartBarIcon, key: 'samplingPercentage' },
  { title: 'Quality %', icon: CheckBadgeIcon, key: 'qualityPercentage' },
  { title: 'Audited %', icon: MagnifyingGlassCircleIcon, key: 'auditedPercentage' },
];

// Main Component
const QACards = ({ filters }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { accessToken } = useAuth();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const baseUrls = getRoundRobinBaseUrl();
        const filteredParams = {};

        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) {
            filteredParams[key] = filters[key];
          }
        });

        const response = await api.get(
          `${baseUrls}/superAdminDashboard/getSuperAdminPercentage`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: filteredParams,
          }
        );

        if (response.data.success) {
          setDashboardData(response.data.data || {});
        } else {
          console.error('Failed to fetch dashboard data');
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (filters) {
      fetchDashboardData();
    }
  }, [filters, accessToken]);

  return (
    <div className="overflow-x-auto">
      <style>
        {`
        @keyframes shimmer {
          0% { background-position: -500px 0; }
          100% { background-position: 500px 0; }
        }

        .shimmer {
          background: linear-gradient(
            to right,
            #f0f0f0 0%,
            #e0e0e0 20%,
            #f0f0f0 40%,
            #f0f0f0 100%
          );
          background-size: 1000px 100%;
          animation: shimmer 1.5s infinite linear;
        }
        `}
      </style>

      <div className="flex space-x-4 p-4 min-w-max">
        {cardData.map(({ title, icon: Icon, key }, index) => (
          <div
            key={index}
            className="bg-white rounded-xl p-4 w-56 text-center shadow-md border border-gray-200 flex-shrink-0 transform hover:scale-105 transition-transform duration-200 ease-in-out"
          >
            {loading ? (
              <div>
                <div className="h-10 w-10 mx-auto mb-2 rounded-full shimmer"></div>
                <div className="h-4 w-24 mx-auto shimmer rounded mb-2"></div>
                <div className="h-4 w-20 mx-auto shimmer rounded"></div>
              </div>
            ) : (
              <>
                <div className="flex justify-center mb-2">
                  <div className="bg-gradient-to-br from-indigo-500 to-indigo-900 p-2 rounded-full shadow">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h2 className="text-sm font-semibold text-gray-700 mb-1">{title}</h2>
                <p className="text-lg font-bold text-gray-900">
                  {dashboardData ? dashboardData[key] ?? 'N/A' : 'N/A'}
                </p>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default QACards;
